import { PropRuntimeGraph, PropRuntimeGraphBody } from '../../../../shared/types/PropRuntimeGraph';
import type { PropInstanceNodeRuntime } from './runtimeNodes/RuntimeNodeTypes';

/**
 * 炼金战斗运行时使用的随机源。
 *
 * 所有节点都通过该接口获取随机数，避免直接调用 `Math.random()`。
 * 真实战斗可以使用默认实现；录像回放、自动化测试和模拟推演应注入带种子的实现。
 */
export interface PropRandomSource {
    /** 返回 `[0, 1)` 范围内的浮点随机数。 */
    next(): number;
    /** 返回 `[min, maxExclusive)` 范围内的整数随机数。 */
    int(min: number, maxExclusive: number): number;
    /** 返回 `[min, max)` 范围内的浮点随机数。 */
    float(min: number, max: number): number;
}

/**
 * 默认随机源。
 *
 * 该实现只适合普通运行；需要复现结果时，应由外部传入稳定随机源。
 */
export const defaultPropRandomSource: PropRandomSource = {
    next: () => Math.random(),
    int: (min, maxExclusive) => Math.floor(Math.random() * Math.max(0, maxExclusive - min)) + min,
    float: (min, max) => Math.random() * (max - min) + min,
};

/**
 * 盘面坐标。
 *
 * `row` 和 `col` 从 0 开始；实例不在盘面上时使用 `-1`。
 */
export interface BoardPosition {
    /** 盘面行号，从 0 开始。 */
    row: number;
    /** 盘面列号，从 0 开始。 */
    col: number;
}

/**
 * 单个道具运行时实例。
 *
 * `PropRuntimeGraph` 是道具原型配置；`RuntimePropInstance` 是一局战斗中的真实对象。
 * 同一原型可以创建多个实例，每个实例拥有独立位置、成长值、保护层和临时状态。
 */
export interface RuntimePropInstance {
    /** 当前战斗内唯一的实例 id，不等同于道具原型 `_id`。 */
    id: string;
    /** 实例当前指向的道具原型；转化动作会替换该字段。 */
    graph: PropRuntimeGraph;
    /** 盘面行坐标；`-1` 表示不在盘面。 */
    row: number;
    /** 盘面列坐标；`-1` 表示不在盘面。 */
    col: number;
    /** 是否仍在运行时有效；死亡或被移除后不再参与普通目标筛选。 */
    alive: boolean;
    /** 永久魔力成长值，由成长动作修改。 */
    grow: number;
    /** 实例累计触发次数，用于调试和兼容简单触发统计。 */
    triggerCount: number;
    /** 主动使用次数，辅料限制使用时可读取该字段。 */
    useCount: number;
    /** 保护层数；被消除时优先消耗保护层，目标不会死亡，但消除动作视为成功。 */
    protectStacks: number;
    /** 是否被净化过；净化动作会设置该标记并清理负面效果。 */
    purified?: boolean;
    /** 收集类辅料暂存的魔力。 */
    collectedMagic: number;
    /** 持续效果、节点扩展和业务标记。 */
    meta: Record<string, unknown>;
    /** 负面效果标记；仅供净化类逻辑使用，不直接参与强度判断。 */
    negativeEffects: string[];
    /** 该实例持有的节点运行时对象；节点内的倒计时、循环次数等跨回合状态保存在这里。 */
    nodeRuntime?: PropInstanceNodeRuntime;
}

/**
 * 真实道具入手时的可选落位参数。
 *
 * 该结构只表达“希望放入的盘面坐标”，不保证一定落位成功。运行时只允许原料进入当前为“空位置”的格子；
 * 如果目标格子不存在、不是空位置，或者入手道具不是原料，则道具只进入对应持有列表。
 */
export interface PropAcquireOptions {
    /** 期望放入的盘面行号。*/
    row?: number;
    /** 期望放入的盘面列号。*/
    col?: number;
}

/**
 * 炼金战斗运行时状态。
 *
 * 状态只保存纯逻辑数据，不包含 Cocos 节点、动画、音效或 UI 对象。
 * 表现层应订阅事件或读取变更记录，再自行播放动画。
 */
/**
 * 永久加成运行时规则。
 *
 * 永久加成不是立即改写目标实例，而是在触发时登记一条规则。之后每轮结算阶段重新执行
 * `actionNode` 的目标输入筛选，并按当前目标逐个追加魔力产出。
 */
export interface RuntimePermanentBoostRule {
    /** 规则唯一 id；有互斥标签时使用 mutexTag，否则使用来源实例和动作节点组合。 */
    id: string;
    /** 创建该规则的来源实例 id。 */
    sourceId: string;
    /** 规则创建时来源实例的原型 id，用于来源被转化后清理旧规则。 */
    sourceGraphId: string;
    /** 互斥标签；同一标签只保留最后登记的一条规则。 */
    actionNodeId: number;
    /** 触发该规则的动作节点定义，用于后续重新读取 targets 输入。 */
    body: PropRuntimeGraphBody;
    /** 每次命中目标时追加的固定魔力。 */

    /** 每次命中目标时按目标基础产出和成长计算的倍率。 */

    /** 来源被移除后是否继续保留；主动使用辅料登记的永久规则需要保留。 */
    keepAfterSourceRemoved: boolean;
}

/**
 * 当前回合基础产出使用的持续加成快照。
 *
 * 永久加成规则会在每轮盘面确定后重新筛选目标，并把命中结果写入该结构。
 * 基础产出结算只读取快照，不在每个道具产出时重新执行筛选，避免随机目标和位置目标在同一轮内反复变化。
 */
export interface RuntimeRoundYieldBoost {
    /** 对 `基础产出 + 成长` 生效的倍率；默认值为 1。 */
    multiplier: number;
    /** 倍率计算后追加的固定魔力；默认值为 0。 */
    add: number;
    /** 提供该加成的来源实例 id，用于调试和表现层展示。 */
    sourceIds: string[];
    /** 命中的永久加成规则 id，用于调试和去重分析。 */
    ruleIds: string[];
}

/**
 * 品质抽取权重运行时规则。
 *
 * 品质权重不是通过“乘上去再除回来”的方式维护，而是记录每个来源产生的规则，
 * 再由规则表重新计算当前品质倍率。这样可以避免多个来源释放顺序不同造成的浮点回退误差。
 */
export interface RuntimeRarityWeightRule {
    /** 规则唯一 id，通常由来源实例 id 和动作节点 id 组成。 */
    id: string;
    /** 创建该规则的来源实例 id。 */
    sourceId: string;
    /** 创建该规则的动作节点 id。 */
    actionNodeId: number;
    /** 被调整的品质值。 */
    rarity: number;
    /** 应用于该品质基础权重的倍率。 */
    multiplier: number;
    /** 来源移除后是否继续保留；主动使用辅料产生的规则会保留。 */
    persistent: boolean;
}

/**
 * 炼金战斗运行时状态。
 *
 * 状态只保存纯逻辑数据，不包含 Cocos 节点、动画、音效或 UI 对象。
 */
export interface BattleRuntimeState {
    /** 盘面行数，默认 4。 */
    rows: number;
    /** 盘面列数，默认 5。 */
    cols: number;
    /** 运行时对外暴露的首行坐标，默认 1。 */
    firstRowIndex: number;
    /** 运行时对外暴露的首列坐标，默认 0。 */
    firstColumnIndex: number;
    /** 当前局内所有仍可被引用的实例，包含材料库、盘面、配方、辅料和空位置实例。 */
    items: RuntimePropInstance[];
    /** 道具原型目录，生成、转化和原型筛选都会查询该表。 */
    catalog: PropRuntimeGraph[];
    /** 材料库中的实例 id；生成和消除会永久改变该数组。 */
    materialLibrary: string[];
    /** 已持有配方实例 id；配方通常不在盘面，但会持续监听事件。 */
    recipes: string[];
    /** 已持有辅料实例 id；辅料通常由主动使用事件触发。 */
    consumables: string[];
    /** 当前盘面实例 id 矩阵；每个格子必须有实例，空格使用数据库中的“空位置”原型实例填充。 */
    board: string[][];
    /** 当前委托序号，从 1 开始。 */
    commissionIndex: number;
    /** 当前委托内回合序号，从 1 开始。 */
    roundIndex: number;
    /** 当前局累计回合序号，从 1 开始。 */
    totalRoundIndex: number;
    /** 当前委托额外增加的调和轮数。 */
    extraCommissionRounds: number;
    /** 当前委托内已经触发过的限一次效果标记。 */
    commissionOnceKeys: Set<string>;
    /** 当前回合即时魔力产出。 */
    currentRoundMagic: number;
    /** 当前局累计魔力。 */
    totalMagic: number;
    /** 品质权重倍率；key 为品质值，value 为累计倍率。 */
    rarityWeightMultipliers: Record<number, number>;
    /** 品质权重规则表；`rarityWeightMultipliers` 由该表重算得到。 */
    rarityWeightRules: RuntimeRarityWeightRule[];
    /** 永久加成互斥标记；key 为 mutexTag，value 表示该类效果已经生效。 */
    permanentBoostRules: RuntimePermanentBoostRule[];
    /** 当前回合基础产出使用的持续加成快照；key 为目标实例 id。 */
    roundYieldBoosts: Record<string, RuntimeRoundYieldBoost>;
}

/**
 * 单个道具实例的快照数据。
 *
 * 快照只保存纯逻辑状态，不保存节点对象、事件监听、UI 状态或动画状态。恢复时会根据 `prototypeId`
 * 重新绑定当前目录中的原型，再重建节点运行时。
 */
export interface BattleRuntimeSnapshotItem {
    /** 局内实例 id；恢复后保持不变，便于外部存档和日志继续引用。 */
    id: string;
    /** 当前实例指向的原型 id。 */
    prototypeId: string;
    /** 实例是否仍然有效；已移除但被永久加成规则引用的来源也会被保留。 */
    alive: boolean;
    /** 快照时所在行；恢复时仅用于判断是否曾在盘面，不保证按原坐标落位。 */
    row: number;
    /** 快照时所在列；恢复时仅用于判断是否曾在盘面，不保证按原坐标落位。 */
    col: number;
    /** 永久成长值。 */
    grow: number;
    /** 累计触发次数。 */
    triggerCount: number;
    /** 主动使用次数。 */
    useCount: number;
    /** 当前保护层数。 */
    protectStacks: number;
    /** 当前是否处于净化状态。 */
    purified?: boolean;
    /** 已收集但尚未收获的魔力。 */
    collectedMagic: number;
    /** 实例扩展状态；必须保持 JSON 可序列化。 */
    meta: Record<string, unknown>;
    /** 负面效果标记列表。 */
    negativeEffects: string[];
    /** 节点实例内部状态；key 为运行时节点 id。 */
    nodeStates: Record<number, unknown>;
}

/**
 * BattleManager 对外提供的存档快照。
 *
 * UI 层负责决定何时持久化该对象、如何写入本地或服务端。BattleManager 只负责把当前运行时状态
 * 序列化成纯数据，并在收到该对象时恢复运行时。
 */
export interface BattleRuntimeSnapshot {
    /** 快照结构版本；仅用于未来兼容判断。 */
    version: 1;
    /** 生成快照的本地时间戳。 */
    savedAt: number;
    /** 盘面行数。 */
    rows: number;
    /** 盘面列数。 */
    cols: number;
    /** 盘面对外暴露的首行坐标。 */
    firstRowIndex: number;
    /** 盘面对外暴露的首列坐标。 */
    firstColumnIndex: number;
    /** 当前委托序号。 */
    commissionIndex: number;
    /** 当前委托内轮次。 */
    roundIndex: number;
    /** 当前局累计轮次。 */
    totalRoundIndex: number;
    /** 当前委托额外轮次。 */
    extraCommissionRounds: number;
    /** 当前回合魔力。 */
    currentRoundMagic: number;
    /** 当前局总魔力。 */
    totalMagic: number;
    /** 当前委托内已触发的限次标记。 */
    commissionOnceKeys: string[];
    /** 材料库实例 id，按入手顺序保存。 */
    materialLibrary: string[];
    /** 配方实例 id，按入手顺序保存。 */
    recipes: string[];
    /** 辅料实例 id，按入手顺序保存。 */
    consumables: string[];
    /** 快照时在盘面上的真实道具实例 id；恢复时随机落入空位。 */
    boardItemIds: string[];
    /** 需要恢复的实例列表。 */
    items: BattleRuntimeSnapshotItem[];
    /** 消耗型辅料等产生的持续加成规则；恢复时直接写回，不重新触发来源效果。 */
    permanentBoostRules: RuntimePermanentBoostRule[];
}

/**
 * 道具运行时事件类型。
 *
 * BattleManager 只负责在流程节点派发事件；PropsMechanicsEngine 监听事件并匹配运行时图入口节点。
 * 事件命名使用过去分词或进行态，表达“流程已经进入某阶段”或“某动作正在尝试发生”，
 * 避免一个事件同时承担多个生命周期含义。
 */
export type PropRuntimeEventType =
    /**
     * 游戏开始事件。
     *
     * 触发时机：一局游戏初始化完成并准备进入第一个委托前。
     * 典型触发方：BattleManager.startGame。
     * 主要用途：初始化全局光环、重置跨局状态、通知表现层进入战斗。
     * 常见字段：通常不携带 item / target；可在 explicitFields 中放入本局规则或选定体系。
     * 是否驱动节点：允许；用于未来全局开局效果。
     * 游戏端理解：这是整局游戏的生命周期起点，只应触发一次。
     */
    | 'gameStarted'
    /**
     * 游戏结束事件。
     *
     * 触发时机：所有委托完成、玩家退出或战斗流程被强制终止时。
     * 典型触发方：BattleManager.endGame。
     * 主要用途：结算全局数据、注销临时监听、释放运行时资源。
     * 常见字段：通常不携带 item / target；explicitFields 可包含结算原因。
     * 是否驱动节点：通常不驱动节点；主要用于清理和结算通知。
     * 游戏端理解：收到该事件后，本局运行时状态不应继续推进。
     */
    | 'gameEnded'
    /**
     * 委托开始事件。
     *
     * 触发时机：进入某一份委托并开始该委托第一轮前。
     * 典型触发方：BattleManager.startCommission。
     * 主要用途：重置“每委托限一次”标记、启用委托级效果、刷新委托状态。
     * 常见字段：commissionIndex 表示当前委托序号。
     * 是否驱动节点：允许；用于未来委托开始类光环。
     * 游戏端理解：这是单个委托的生命周期起点，同一局内会触发多次。
     */
    | 'commissionStarted'
    /**
     * 委托结束事件。
     *
     * 触发时机：当前委托全部轮次结算完成后。
     * 典型触发方：BattleManager.endCommission。
     * 主要用途：结算委托产出、触发委托结束奖励、清理委托级状态。
     * 常见字段：commissionIndex 表示结束的委托序号。
     * 是否驱动节点：通常不驱动节点；主要用于结算和清理通知。
     * 游戏端理解：该事件之后通常进入配方选择或下一委托准备阶段。
     */
    | 'commissionEnded'
    /**
     * 回合开始事件。
     *
     * 触发时机：每轮盘面抽取并填充完成后、基础魔力结算前。
     * 典型触发方：BattleManager.runRound。
     * 主要用途：通知表现层和统计层进入回合开始阶段；道具回合开始入口由 BattleManager 按顺序直接调用。
     * 常见字段：commissionIndex、roundIndex、totalRoundIndex。
     * 是否驱动节点：不通过事件总线驱动；`eventTurnStart` 由 BattleManager 直接触发。
     * 游戏端理解：道具主体逻辑通常在该阶段执行，节点可以修改盘面、材料库和当前回合魔力。
     */
    | 'roundStarted'
    /**
     * 回合结束事件。
     *
     * 触发时机：当前回合节点效果和基础魔力结算完成后。
     * 典型触发方：BattleManager.runRound。
     * 主要用途：清理回合临时效果、触发回合末监听、通知表现层完成本轮。
     * 常见字段：commissionIndex、roundIndex、totalRoundIndex。
     * 是否驱动节点：默认不驱动当前节点；除非后续明确设计回合结束节点。
     * 游戏端理解：该事件之后当前回合不应再追加普通产出，除非明确设计为回合结束效果。
     */
    | 'roundEnded'
    /**
     * 道具入手事件。
     *
     * 触发时机：玩家获得一个道具实例后。
     * 典型触发方：BattleManager.acquireProp 或奖励选择流程。
     * 主要用途：触发持有类、入手类或永久权重类效果。
     * 常见字段：item 为新获得的道具实例。
     * 是否驱动节点：不直接驱动节点；`eventAcquire` 在实例化节点运行时时立即执行。
     * 游戏端理解：该事件表示实例已经进入对应持有列表，后续可被材料库、辅料或配方逻辑引用。
     */
    | 'propAcquired'
    /**
     * 道具移除事件。
     *
     * 触发时机：道具从玩家持有列表或运行时状态中被移除后。
     * 典型触发方：BattleManager.removeProp。
     * 主要用途：撤销持有效果、清理实例状态、通知表现层更新持有列表。
     * 常见字段：item 为被移除的道具实例。
     * 是否驱动节点：通常不驱动节点；用于清理持有状态和表现。
     * 游戏端理解：该事件不同于消除；移除更偏系统层或持有层操作。
     */
    | 'propRemoved'
    /**
     * 道具进入盘面事件。
     *
     * 触发时机：实例被放入盘面坐标后。
     * 典型触发方：BattleManager.placeItem 或盘面抽取流程。
     * 主要用途：通知表现层创建或移动盘面表现，后续也可扩展为进场触发。
     * 常见字段：item 为进入盘面的实例。
     * 是否驱动节点：默认不驱动节点；避免抽取摆盘阶段产生递归效果。
     * 游戏端理解：该事件只表示位置进入盘面，不代表道具被新获得。
     */
    | 'propEnteredBoard'
    /**
     * 道具离开盘面事件。
     *
     * 触发时机：实例从盘面坐标移出后。
     * 典型触发方：位置交换、转化、消除或回合重抽流程。
     * 主要用途：通知表现层移除盘面表现、清理位置相关临时效果。
     * 常见字段：item 为离开盘面的实例；source 可表示触发来源。
     * 是否驱动节点：默认不驱动节点；用于表现和位置缓存更新。
     * 游戏端理解：离开盘面不一定死亡，也可能只是回到材料库或被移动。
     */
    | 'propLeftBoard'
    /**
     * 道具计时器变化事件。
     *
     * 触发时机：回合开始节点初始化、回合推进、加速/延迟动作修改倒计时，或道具离开盘面需要清除显示时。
     * 常见字段：item 为计时器所属实例；timerValue 为当前显示值，undefined 表示清除该实例的计时器显示。
     * 游戏端理解：这是表现层状态事件，不直接驱动节点逻辑；盘面 UI 应通过该事件维护计时器显示。
     */
    | 'propTimerChanged'
    /**
     * 道具被消除事件。
     *
     * 触发时机：真实道具实例被消除并标记为失效后。
     * 典型触发方：BattleManager.eliminateProp 或 actionEliminate 节点。
     * 主要用途：触发“被消除时”节点、生成衍生物、播放消除表现。
     * 常见字段：item / target 通常都指向被消除实例；source 表示消除来源。
     * 是否驱动节点：允许；`eventEliminated` 节点监听该事件。
     * 游戏端理解：受保护目标不会死亡，因此不会派发该事件；但消除动作本身仍可视为成功。
     */
    | 'propEliminated'
    /**
     * 道具转化事件。
     *
     * 触发时机：实例当前原型被替换为另一个原型后。
     * 典型触发方：actionTransform 节点。
     * 主要用途：通知表现层更新资源、记录转化日志、刷新原型相关缓存。
     * 常见字段：item / target 为被转化实例；source 为转化来源；explicitFields.prototypeId 为新原型。
     * 是否驱动节点：默认不驱动节点；避免转化链无意递归。
     * 游戏端理解：实例 id 不变，但 `item.graph` 已经指向新原型。
     */
    | 'propTransformed'
    /**
     * 道具位置交换事件。
     *
     * 触发时机：actionSwapPosition 节点直接交换两个盘面实例坐标后。
     * 典型触发方：actionSwapPosition 节点。
     * 主要用途：通知表现层刷新或播放位置交换动画。
     * 常见字段：item / target 为当前交换实例；relatedItem 为另一方；row / col 为交换后坐标；previousRow / previousCol 为交换前坐标。
     * 是否驱动节点：默认不驱动节点；这是位置结果事件，不代表原型变化。
     * 游戏端理解：实例 id 与原型均不变，只是盘面坐标发生交换。
     */
    | 'propPositionSwapped'
    /**
     * 道具生成事件。
     *
     * 触发时机：生成动作创建新实例并完成入手流程后。
     * 典型触发方：actionSpawn 节点。
     * 主要用途：表达“某个动作生成了某个道具”的语义，用于日志、回放、调试和生成表现。
     * 常见字段：item 为新生成实例；source 为执行生成动作的来源实例；prototype / prototypeId 为生成原型。
     * 是否驱动节点：默认不驱动节点；生成实例的入手和进场仍分别由 `propAcquired`、`propEnteredBoard` 表达。
     * 游戏端理解：该事件不替代入手或进场事件，只补充说明该实例来自生成动作。
     */
    /**
     * 道具被主动使用事件。
     *
     * 触发时机：玩家主动使用一个辅料或可使用道具时。
     * 典型触发方：BattleManager.useProp。
     * 主要用途：触发主动使用节点、释放辅料效果、处理使用后消耗。
     * 常见字段：item 为被使用的道具实例。
     * 是否驱动节点：允许；`eventUse` 节点监听该事件。
     * 游戏端理解：该事件表示使用动作已经成立，默认辅料会在事件处理后被移除。
     */
    | 'propUsed'
    /**
     * 道具魔力收集事件。
     *
     * 触发时机：actionCollect 节点从单个目标成功收集魔力后。
     * 典型触发方：actionCollect 节点。
     * 主要用途：通知表现层从目标到来源播放收集动画，或在日志中记录收集来源和收集量。
     * 常见字段：item / source 为执行收集的来源实例；target 为被收集目标；targetIds 记录目标 id；magicDelta 为本次收集到来源实例的魔力量。
     * 是否驱动节点：默认不驱动节点；收集只是把魔力暂存到来源实例，不直接改变当前回合魔力。
     * 游戏端理解：目标不会减少魔力，来源实例的 collectedMagic 已经增加。
     */
    | 'propCollected'
    /**
     * 魔力变化事件。
     *
     * 触发时机：当前回合魔力或累计魔力发生变化后。
     * 典型触发方：PropsMechanicsEngine.addMagic、BattleManager 基础产出结算。
     * 主要用途：通知表现层播放产出数字、刷新魔力 UI、记录战斗日志。
     * 常见字段：magicDelta 为变化量；source 为来源实例；explicitFields.targetIds 可记录受影响目标。
     * 是否驱动节点：不驱动节点；这是结果事件。
     * 游戏端理解：这是结果事件，不应再次驱动会改变魔力的节点，避免递归产出。
     */
    | 'magicChanged'
    /**
     * 品质抽取权重变化事件。
     *
     * 触发时机：品质权重调整动作生效或被释放后。
     * 典型触发方：actionRarityWeight 节点。
     * 主要用途：刷新品质抽取概率显示、重新计算三选一候选品质分布。
     * 常见字段：source 为产生或释放权重效果的道具实例。
     * 是否驱动节点：不驱动节点；这是全局抽取状态变化通知。
     * 游戏端理解：该事件只说明抽取权重配置发生变化，不代表已经抽取了任何道具。
     */
    | 'rarityWeightChanged'
    /**
     * 永久加成规则变化事件。
     *
     * 触发时机：永久加成动作生效或对应持有效果被释放后。
     * 典型触发方：actionPermanentBoost 节点。
     * 主要用途：刷新全局持续加成列表，并让后续产出计算使用最新规则。
     * 常见字段：source 为提供永久加成规则的道具实例。
     * 是否驱动节点：不驱动节点；这是持续规则集合变化通知。
     * 游戏端理解：永久加成通常不会立即产生魔力，而是在后续结算中影响匹配目标。
     */
    | 'permanentBoostRulesChanged'
    /**
     * 当前委托调和轮数上限变化事件。
     *
     * 触发时机：延长调和动作成功增加当前委托轮数后。
     * 典型触发方：actionExtendCommission 节点。
     * 主要用途：刷新委托轮次显示，并让流程判断使用新的轮数上限。
     * 常见字段：source 为产生轮数变化的道具实例。
     * 是否驱动节点：不驱动节点；这是流程参数变化通知。
     * 游戏端理解：该事件只改变当前委托可进行的轮数，不会直接开始新回合。
     */
    | 'commissionRoundLimitChanged'
    /**
     * 动作动画触发事件。
     *
     * 触发时机：动作节点成功执行，并且该动作配置了自身特效或目标特效后。
     * 典型触发方：任意 action 节点，由运行时在动作成功后统一派发。
     * 主要用途：表现层播放动作特效；自身特效通常播放在 source，目标特效播放在 target。
     * 常见字段：animationPaths 为动画资源路径列表；effectPaths 为动画伴随的特效资源路径列表；animationScope 标识自身或目标动画；
     * actionNodeId / actionType 标识来源动作节点；targetIds 标识本次特效落点。
     * 是否驱动节点：不驱动节点；这是纯表现事件。
     * 游戏端理解：该事件不改变战斗状态，监听方不应在此事件中再次执行节点逻辑。
     */
    | 'animationTriggered';

/**
 * 运行时事件原因。
 *
 * `type` 描述事件发生了什么，`reason` 描述事件为什么发生。两者分离后，监听方可以按事件类型处理业务，
 * 同时用原因区分同类事件的来源路径，例如普通移除、快照恢复、动作节点触发或计时器刷新。
 */
export type PropRuntimeEventReason =
    /**
     * 初始占位原因。
     *
     * 仅用于节点上下文或临时事件对象创建时填充默认值；正常运行流程中不应依赖该原因执行业务逻辑。
     */
    | 'initial'
    /**
     * 快照恢复过程中的状态重建。
     *
     * 表示事件来自恢复历史快照，而不是玩家操作、节点动作或正常回合推进。
     */
    | 'snapshotRestore'
    /**
     * 快照恢复完成后的状态同步。
     *
     * 主要用于恢复后重新刷新计时器等显示状态，不代表道具重新入手或重新触发效果。
     */
    | 'snapshotRestored'
    /**
     * 游戏开始流程触发。
     *
     * 由 BattleManager 启动整局游戏时产生，用于区分委托、回合之外的全局生命周期事件。
     */
    | 'gameStarted'
    /**
     * 游戏结束流程触发。
     *
     * 由 BattleManager 结束整局游戏并准备释放运行时资源时产生。
     */
    | 'gameEnded'
    /**
     * 委托开始流程触发。
     *
     * 表示进入某个委托阶段，通常发生在配方选择和本委托回合推进之前。
     */
    | 'commissionStarted'
    /**
     * 委托结束流程触发。
     *
     * 表示当前委托的所有回合已经结算完成，准备进入委托结算或下一委托。
     */
    | 'commissionEnded'
    /**
     * 回合开始流程触发。
     *
     * 表示 BattleManager 开始推进当前回合的全局流程，不等同于某个具体道具的回合开始节点。
     */
    | 'roundStarted'
    /**
     * 回合结束流程触发。
     *
     * 表示当前回合的产出、节点效果和回合末清理已经结束。
     */
    | 'roundEnded'
    /**
     * 具体道具的回合开始入口被执行。
     *
     * BattleManager 按配方、辅料、盘面原料顺序调用道具实例时使用该原因。
     */
    | 'propTurnStart'
    /**
     * 道具通过统一入手流程进入运行时。
     *
     * 生成、三选一、初始材料、快照恢复以外的正常入手流程均应使用统一入手语义。
     */
    | 'propAcquired'
    /**
     * 道具生成动作产生的入手或自动入场。
     *
     * 表示 actionSpawn 节点通过统一入手管线创建了新的道具实例，并可能因为盘面存在空位置而自动落位。
     */
    | 'actionSpawn'
    /**
     * 外部或系统直接移除道具。
     *
     * 该原因表示调用 removeProp 的强制移除语义，不触发保护判断，也不触发尝试消除或被消除流程。
     */
    | 'removeProp'
    /**
     * 玩家或外部系统主动使用道具。
     *
     * 该原因只用于 BattleManager.useProp 触发的主动使用流程。
     */
    | 'useProp'
    /**
     * 外部或节点发起消除流程。
     *
     * 该原因会进入消除语义，可受到保护影响，并可能触发被消除事件节点。
     */
    | 'eliminateProp'
    /**
     * 清空盘面导致道具离场。
     *
     * 主要用于回合结束、快照恢复或整局重置时的盘面清理。
     */
    | 'clearBoard'
    /**
     * 放置道具时替换了原位置上的实例。
     *
     * 表示目标格已有实例，需要先让旧实例离场，再把新实例放入该位置。
     */
    | 'placeItemReplace'
    /**
     * 道具被放入盘面格。
     *
     * 表示实例从材料库、生成流程或恢复流程进入盘面。
     */
    | 'placeItem'
    /**
     * 交换位置动作导致实例盘面坐标变化。
     *
     * 该原因表示 actionSwapPosition 节点直接交换两个盘面实例的坐标；不会触发离场或入场语义。
     */
    | 'actionSwapPosition'
    /**
     * 道具从盘面离场。
     *
     * 表示实例离开盘面格，并由空位置实例补位。
     */
    | 'removeFromBoard'
    /**
     * 魔力数值发生变化。
     *
     * 用于基础产出、额外产出、收获等会改变本轮或总魔力的流程。
     */
    | 'magicChanged'
    /**
     * 回合产出前准备持续加成快照。
     *
     * 用于把永久加成规则固化到当前回合的目标实例，避免转化等后续变化影响本轮加成归属。
     */
    | 'prepareRoundYieldBoosts'
    /**
     * 单条永久加成规则被移除。
     *
     * 表示某条规则因生命周期结束或显式释放而失效。
     */
    | 'permanentBoostRuleRemoved'
    /**
     * 指定来源的永久加成规则被批量移除。
     *
     * 通常用于持有触发类效果释放时，按来源道具实例清理对应规则。
     */
    | 'permanentBoostRuleRemovedBySource'
    /**
     * 永久加成动作生效。
     *
     * 表示 actionPermanentBoost 节点写入了新的持续加成规则。
     */
    | 'actionPermanentBoost'
    /**
     * 转化动作生效。
     *
     * 表示 actionTransform 节点将目标实例重置为新的原型。
     */
    | 'actionTransform'
    /**
     * 消除动作生效。
     *
     * 表示 actionEliminate 节点对目标执行了消除流程。
     */
    | 'actionEliminate'
    /**
     * 收集动作生效。
     *
     * 表示 actionCollect 节点从目标处收集魔力并存入来源实例的 collectedMagic。
     */
    | 'actionCollect'
    /**
     * 品质权重效果释放。
     *
     * 表示持有触发产生的品质权重规则被移除。
     */
    | 'releaseRarityWeight'
    /**
     * 品质权重动作生效。
     *
     * 表示 actionRarityWeight 节点新增或更新了品质抽取权重规则。
     */
    | 'actionRarityWeight'
    /**
     * 延长调和动作生效。
     *
     * 表示 actionExtendCommission 节点增加了当前委托可进行的回合数。
     */
    | 'actionExtendCommission'
    /**
     * 动作动画事件触发。
     *
     * 表示动作节点执行成功后，向表现层派发自身动画或目标动画。
     */
    | 'animationTriggered'
    /**
     * 位置条件提示动画触发。
     *
     * 表示运行时检测到“绝对位置目标集合包含自身”的位置条件，并根据当前坐标触发位置正确或位置错误提示。
     * 该原因只用于表现层动画，不会驱动节点，也不会改变战斗状态。
     */
    | 'positionEffectChanged'
    /**
     * 道具入手后刷新计时器显示。
     *
     * 只用于回合开始事件节点的计时器显示同步，不代表节点效果触发。
     */
    | 'acquired'
    /**
     * 道具回合开始节点推进计时器。
     *
     * 表示 EventTurnStartNode 在道具自己的回合开始入口中扣减倒计时。
     */
    | 'turnStart'
    /**
     * 加速动作改变计时器。
     *
     * 表示 actionAccelerate 节点调整了目标的回合开始计时器。
     */
    | 'accelerate'
    /**
     * 计时器触发后重置。
     *
     * 表示 EventTurnStartNode 执行后续 flow 后，把倒计时恢复到下一个周期。
     */
    | 'reset'
    /**
     * 转化后刷新新原型的计时器显示。
     *
     * 表示转化动作重建了目标节点运行时，需要向显示层同步新的初始计时器。
     */
    | 'transformed';

/**
 * 道具运行时事件。
 *
 * 事件数据全部使用显式字段表达。监听方应按 `type` 判断事件语义，并读取对应字段；
 * 不应通过通用 explicitFields 传递运行时快照，避免事件类型失去约束力。
 */

export interface PropRuntimeEvent {
    /** 事件类型。 */
    type: PropRuntimeEventType;
    /** 事件主实例；主动使用、入手、被消除事件通常会携带该字段。 */
    item?: RuntimePropInstance;
    /** 事件目标实例；消除、转化等动作可用该字段传递被影响目标。 */
    target?: RuntimePropInstance;
    /** 与当前事件主实例相关联的另一个实例；交换位置事件中表示互换位置的另一方。*/
    relatedItem?: RuntimePropInstance;
    /** 触发事件的来源实例。 */
    source?: RuntimePropInstance;
    /** 事件关联的新原型，常用于转化和生成事件。 */
    prototype?: PropRuntimeGraph;
    /** 转化前的旧原型；仅用于转化事件记录和表现层日志。 */
    previousPrototype?: PropRuntimeGraph;
    /** 当前委托序号。 */
    commissionIndex?: number;
    /** 当前委托内回合序号。 */
    roundIndex?: number;
    /** 当前局累计回合序号。 */
    totalRoundIndex?: number;
    /** 魔力变化量。 */
    magicDelta?: number;
    /** 受影响的目标实例 id 列表。 */
    targetIds?: string[];
    /** 需要播放的动画资源路径列表。 */
    animationPaths?: string[];
    /** 需要随动画一起播放的特效资源路径列表。 */
    effectPaths?: string[];
    /** 动画落点类型：self 表示自身动画，target 表示目标动画。 */
    animationScope?: 'self' | 'target';
    /** 触发特效的动作节点运行时 id。 */
    actionNodeId?: number;
    /** 触发特效的动作节点类型。 */
    actionType?: string;
    /** 道具计时器当前显示值；undefined 表示清除该实例计时器显示。 */
    timerValue?: number;
    /** 盘面行数。 */
    /** 盘面列数。 */
    /** 盘面对外暴露的首行坐标。 */
    /** 盘面对外暴露的首列坐标。 */
    /** 当前盘面实例矩阵。 */
    /** 当前运行时保留的全部实例。 */
    /** 材料库中的存活实例。 */
    /** 配方列表中的存活实例。 */
    /** 辅料列表中的存活实例。 */
    /** 当前回合魔力。 */
    /** 当前局累计魔力。 */
    /** 当前品质权重倍率。 */
    /** 当前品质抽取概率百分比，顺序为普通、少见、稀有、罕见。 */
    /** 当前生效的永久加成规则。 */
    /** 当前委托内已生效的限次标记。 */
    /** 变化的盘面行坐标。 */
    row?: number;
    /** 变化的盘面列坐标。 */
    col?: number;
    /** 变化前的盘面行坐标；用于位置交换等需要表达移动前后位置的事件。*/
    previousRow?: number;
    /** 变化前的盘面列坐标；用于位置交换等需要表达移动前后位置的事件。*/
    previousCol?: number;
    /** 状态变化原因，用于日志和调试。 */
    reason: PropRuntimeEventReason;
    /** 状态变化关联的实例 id。 */
    itemId?: string;
    /** 变化关联的原型 id。 */
    prototypeId?: string;
    /** 转化前旧原型 id；仅用于转化事件记录和表现层日志。 */
    previousPrototypeId?: string;
}

/**
 * 节点数据端口允许返回的值。
 *
 * 流程端口不通过该类型返回；流程端口由引擎在动作节点执行完成后推进。
 */
export type PropNodeValue =
    | RuntimePropInstance[]
    | PropRuntimeGraph[]
    | string[]
    | number
    | boolean
    | null
    | undefined;

/**
 * 动作节点执行结果。
 *
 * `success` 决定动作节点后续走 `complete` 还是 `fail`；
 * `eachTargets` 用于动作逐个目标生效时推进 `each` 分支。
 */
export interface PropActionResult {
    /** 动作是否成功触发。 */
    success: boolean;
    /** 每次生效的目标集合，用于推进 each 流程。 */
    eachTargets?: RuntimePropInstance[];
    /** 动作节点可选返回的数据，通常用于调试。 */
    value?: PropNodeValue;
}

/**
 * 持有触发效果的释放回调。
 *
 * 由持有触发事件产生的持续效果，应在来源道具被移除时通过该回调撤销。
 * 当前只有明确支持反向撤销的动作节点执行真实逻辑；其它动作会登记警告型回调。
 */
export type PropRuntimeReleaseCallback = () => void;

/**
 * 逻辑效果记录。
 *
 * 该记录不负责播放动画；表现层可以订阅事件或读取 effect，再按 `sourceId` / `targetIds` 播放表现。
 */
export type PropRuntimeEffect =
    | { kind: 'grow'; sourceId: string; targetIds: string[]; amount: number }
    | { kind: 'extraYield'; sourceId: string; targetIds: string[]; amount: number }
    | { kind: 'protect'; sourceId: string; targetIds: string[]; stacks: number }
    | { kind: 'purify'; sourceId: string; targetIds: string[] }
    | { kind: 'accelerate'; sourceId: string; targetIds: string[]; amount: number }
    | { kind: 'eliminate'; sourceId: string; targetIds: string[]; blockedIds: string[] }
    | { kind: 'transform'; sourceId: string; targetIds: string[]; prototypeIds: string[] }
    | { kind: 'spawn'; sourceId: string; prototypeIds: string[]; targetIds: string[]; count: number }
    | { kind: 'collect'; sourceId: string; targetIds: string[]; amount: number }
    | { kind: 'harvest'; sourceId: string; amount: number }
    | { kind: 'rarityWeight'; sourceId: string; rarity: number; multiplier: number }
    | { kind: 'extendCommission'; sourceId: string; count: number }
    | { kind: 'swapPosition'; sourceId: string; targetIds: string[] }
    | { kind: 'permanentBoost'; sourceId: string; targetIds: string[]; ruleId?: string };

/**
 * 运行时状态变更记录。
 *
 * 变更记录用于调试、表现同步和未来存档；它描述已经发生的结果，不再重复执行逻辑。
 */
export type PropRuntimeMutation =
    | { kind: 'alive'; itemId: string; alive: boolean; sourceId: string }
    | { kind: 'position'; itemId: string; row: number; col: number; sourceId: string }
    | { kind: 'grow'; itemId: string; grow: number; delta: number; sourceId: string }
    | { kind: 'protect'; itemId: string; protectStacks: number; delta: number; sourceId: string }
    | { kind: 'purified'; itemId: string; purified: boolean; sourceId: string }
    | { kind: 'graph'; itemId: string; prototypeId: string; sourceId: string }
    | { kind: 'meta'; itemId: string; key: string; value: unknown; sourceId: string }
    | { kind: 'magic'; delta: number; currentRoundMagic: number; totalMagic: number; sourceId: string };

/**
 * 单次事件派发后的引擎结果。
 *
 * 调用方可用它做日志、调试面板、战斗回放或表现层批处理。
 */
export interface PropMechanicsDispatchResult {
    /** 本次派发的原始事件。 */
    event: PropRuntimeEvent;
    /** 本次事件产生的逻辑效果。 */
    effects: PropRuntimeEffect[];
    /** 本次事件造成的状态变更。 */
    mutations: PropRuntimeMutation[];
    /** 本次执行期间出现的非致命警告。 */
    warnings: string[];
    /** 持有触发效果释放时需要执行的回调。 */
    releaseCallbacks?: PropRuntimeReleaseCallback[];
}

/**
 * 判断节点类型是否属于动作节点。
 *
 * 动作节点拥有流程输出，并且是唯一允许修改战斗状态的节点类型。
 */
export function isPropActionNode(type: string): boolean {
    return type.indexOf('action') === 0;
}
